import React, { useState, useEffect, useRef } from 'react';
import { Slider, Tooltip } from 'antd';
import message from '../../../utils/message';
import styles from './EditProfileModal.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, uploadAvatar } from '../../../reduxStore/actions/userActionsHelper';
import {
    userLoading$,
    userError$,
    userUploadingAvatar$,
    userUploadedAvatarUrl$,
    userUploadAvatarError$
} from '../../../reduxStore/selectors/userSelectors';

const EditProfileModal = ({ open, onClose, user }) => {
    const dispatch = useDispatch();
    const isLoading = useSelector(userLoading$);
    const error = useSelector(userError$);
    const isUploadingAvatar = useSelector(userUploadingAvatar$);
    const uploadedAvatarUrl = useSelector(userUploadedAvatarUrl$);
    const uploadAvatarError = useSelector(userUploadAvatarError$);
    const [username, setUsername] = useState(user?.username || '');
    const [job, setJob] = useState(user?.job || '');
    const [targetScore, setTargetScore] = useState(user?.target_score || 1200);
    const [changed, setChanged] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
    const [avatarFile, setAvatarFile] = useState(null);
    const [wasLoading, setWasLoading] = useState(false);
    const [wasUploadingAvatar, setWasUploadingAvatar] = useState(false);
    const fileInputRef = useRef(null);

    // Use user's avatar or fall back to Prepal logo
    const prepalLogo = require('../../../publicAsset/media/Prepal Logo 3.svg').default;
    const displayAvatar = avatarUrl || prepalLogo;

    useEffect(() => {
        setUsername(user?.username || '');
        setJob(user?.job || '');
        setTargetScore(user?.target_score || 1200);
        setAvatarUrl(user?.avatar || '');
        setAvatarFile(null);
        setChanged(false);
    }, [user, open]);

    // Track when update completes
    useEffect(() => {
        if (wasLoading && !isLoading) {
            // Update just completed
            if (!error) {
                // Success - no error
                message.success('Profile updated successfully');
            } else {
                // Error occurred
                message.error(error || 'Failed to update profile');
            }
        }
        setWasLoading(isLoading);
    }, [isLoading, wasLoading, error]);

    // Track when avatar upload completes
    useEffect(() => {
        if (wasUploadingAvatar && !isUploadingAvatar) {
            if (!uploadAvatarError && uploadedAvatarUrl) {
                // Avatar uploaded successfully, now update user profile
                message.success('Avatar uploaded successfully');
                dispatch(updateUser.request({
                    id: user?.id,
                    userData: {
                        username,
                        job,
                        target_score: targetScore,
                        avatar: uploadedAvatarUrl,
                        updated_at: user?.updated_at
                    }
                }));
            } else if (uploadAvatarError) {
                message.error(uploadAvatarError || 'Avatar upload failed');
            }
        }
        setWasUploadingAvatar(isUploadingAvatar);
    }, [isUploadingAvatar, wasUploadingAvatar, uploadedAvatarUrl, uploadAvatarError, dispatch, user, username, job, targetScore]);

    if (!open) return null;

    const handleSave = () => {
        if (!changed || isLoading || isUploadingAvatar) return;

        if (avatarFile) {
            // Upload avatar first
            const formData = new FormData();
            formData.append('avatar', avatarFile);
            if (user?.avatar) {
                formData.append('oldAvatarUrl', user.avatar);
            }
            dispatch(uploadAvatar.request(formData));
        } else {
            // No avatar to upload, just update profile
            dispatch(updateUser.request({
                id: user?.id,
                userData: {
                    username,
                    job,
                    target_score: targetScore,
                    avatar: avatarUrl,
                    updated_at: user?.updated_at
                }
            }));
        }
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
        setChanged(e.target.value !== user?.username || job !== user?.job || targetScore !== user?.target_score || avatarFile !== null);
    };
    const handleJobChange = (e) => {
        setJob(e.target.value);
        setChanged(username !== user?.username || e.target.value !== user?.job || targetScore !== user?.target_score || avatarFile !== null);
    };
    const handleTargetScoreChange = (value) => {
        setTargetScore(value);
        setChanged(username !== user?.username || job !== user?.job || value !== user?.target_score || avatarFile !== null);
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type - Accept only common image formats for Cloudinary upload
            const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!acceptedTypes.includes(file.type)) {
                message.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size - Max 5MB
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                message.error('Image size must be less than 5MB');
                return;
            }

            setAvatarFile(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                setAvatarUrl(event.target.result);
                message.success('Image loaded for preview.');
            };
            reader.onerror = () => {
                message.error('Failed to load image for preview.');
            };
            reader.readAsDataURL(file);
            setChanged(true);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Edit Profile</h2>
                    <button className={styles.closeButton} onClick={onClose}>×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.avatarSection}>
                        <div className={styles.avatarWrapper}>
                            <img src={displayAvatar} alt="Avatar" className={styles.avatar} />
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={handleAvatarChange}
                        />
                        <button
                            className={styles.uploadButton}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingAvatar}
                        >
                            {isUploadingAvatar && <span className={styles.spinner}></span>}
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
                            </svg>
                            {isUploadingAvatar ? 'Uploading...' : 'Upload Avatar'}
                        </button>
                        <p className={styles.uploadHint}>
                            Accepted formats: JPEG, PNG, GIF, WebP (Max 5MB)
                        </p>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>Email</label>
                        <input
                            type="email"
                            className={styles.input}
                            value={user?.email || ''}
                            disabled
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>Username</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={username}
                            onChange={handleUsernameChange}
                            placeholder="Enter your username"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>Job</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={job}
                            onChange={handleJobChange}
                            placeholder="Enter your job title"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <div className={styles.sliderHeader}>
                            <label className={styles.label}>SAT Target Score</label>
                            <span className={styles.scoreValue}>{targetScore}</span>
                        </div>
                        <Slider
                            min={400}
                            max={1600}
                            step={10}
                            value={targetScore}
                            onChange={handleTargetScoreChange}
                            marks={{
                                400: '400',
                                1000: '1000',
                                1600: '1600'
                            }}
                            tooltip={{
                                open: false
                            }}
                        />
                    </div>
                </div>

                <div className={styles.supportMessage}>
                    <Tooltip title="Thank you for supporting Prepal with this Early Access, please contact us if you encounter any difficulties with your account during this period" placement="top">
                        <svg className={styles.infoIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="7" stroke="#777e90" strokeWidth="1.5" />
                            <path d="M8 7V11M8 5V5.5" stroke="#777e90" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </Tooltip>
                    <span className={styles.messageText}>Thank you for supporting Prepal with this Early Access</span>
                </div>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.cancelButton}`}
                        onClick={onClose}
                        disabled={isUploadingAvatar || isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className={`${styles.button} ${styles.saveButton}`}
                        onClick={handleSave}
                        disabled={!changed || isUploadingAvatar || isLoading}
                    >
                        {(isUploadingAvatar || isLoading) && <span className={styles.spinner}></span>}
                        {isUploadingAvatar ? 'Uploading...' : isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditProfileModal;
